import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import procurementService, {
  AtRiskTaskResource,
  PurchaseOrder,
} from '../../services/api/procurementService';

const fmtCurrency = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'JMD', maximumFractionDigits: 0 });

function isLate(po: PurchaseOrder) {
  if (!po.expectedDeliveryDate || ['received', 'closed', 'cancelled'].includes(po.status)) return false;
  return new Date(po.expectedDeliveryDate).getTime() < Date.now();
}

export default function ProcurementPipelineWidget({ projectId }: { projectId?: string }) {
  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [atRisk, setAtRisk] = useState<AtRiskTaskResource[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      procurementService.listPurchaseOrders({ projectId, limit: 10 }),
      procurementService.getAtRisk({ projectId, withinDays: 14 }),
    ])
      .then(([poResult, riskRows]) => {
        if (cancelled) return;
        setPos(poResult.rows);
        setAtRisk(riskRows);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || 'Failed to load procurement pipeline');
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const totalCommitted = pos.reduce((sum, po) => sum + Number(po.totalAmount || 0), 0);
  const lateDeliveries = pos.filter(isLate).length;
  const atRiskCritical = atRisk.filter((row) => row.procurementStatus === 'not_ordered' || row.procurementStatus === 'on_order_late');

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Procurement pipeline
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <Typography variant="overline" color="text.secondary">Open POs</Typography>
          <Typography variant="h5">{pos.length}</Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography variant="overline" color="text.secondary">Committed</Typography>
          <Typography variant="h5">{fmtCurrency(totalCommitted)}</Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography variant="overline" color="text.secondary">At Risk</Typography>
          <Typography variant="h5" color={atRiskCritical.length > 0 ? 'error.main' : 'text.primary'}>
            {atRiskCritical.length}
          </Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography variant="overline" color="text.secondary">Late POs</Typography>
          <Typography variant="h5" color={lateDeliveries > 0 ? 'warning.main' : 'text.primary'}>
            {lateDeliveries}
          </Typography>
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>Procurement risk</Typography>
      <List dense disablePadding>
        {atRiskCritical.slice(0, 5).map((row) => (
          <ListItem key={`${row.taskId}-${row.resourceId}`} disableGutters>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <span>{row.taskCode || row.taskTitle || row.taskId}</span>
                  <Chip
                    size="small"
                    color={row.procurementStatus === 'not_ordered' ? 'error' : 'warning'}
                    label={row.procurementStatus.replace(/_/g, ' ')}
                  />
                </Box>
              }
              secondary={`${row.resourceName || row.resourceId} shortage ${row.shortage}`}
            />
          </ListItem>
        ))}
        {atRiskCritical.length === 0 && (
          <Typography variant="body2" color="text.secondary">No procurement risk in the selected window.</Typography>
        )}
      </List>
    </Paper>
  );
}
